import type { EntityType, TopicAction, WorkflowType } from '@communities/content-management/domain';
import {
  useCommunityWorkflows,
  useCommunityGroupWorkflows,
  useUpsertCommunityWorkflow,
  useUpsertCommunityGroupWorkflow,
} from '@communities/content-management/hooks';
import { isCommunity, isClassificationWorkflow } from '@communities/content-management/utils';
import { SectionHeader, DataList } from '@core/components';
import { IconButtonSize, IconSize, Info16, Badge, Button, IconButton } from '@soteria/design-system-ve';
import { useCallback, useMemo, useState } from 'react';

import { TopicActionDialog } from './TopicActionDialog';
import * as styles from './TopicActionsSection.css';
import { WorkflowMoreInfoDialog } from './WorkflowMoreInfoDialog';

interface TopicActionsSectionProps {
  workflowType: WorkflowType;
  entityId: string;
  entityType: EntityType;
  isEntityAdmin: boolean;
}

/**
 * Self-contained topic actions section that manages its own data and state
 */

// eslint-disable-next-line complexity
export function TopicActionsSection({ workflowType, entityId, entityType, isEntityAdmin }: TopicActionsSectionProps) {
  const isForCommunity = isCommunity(entityType);

  // Hooks for workflows
  const communityWorkflowsHook = useCommunityWorkflows(isForCommunity ? entityId : undefined, workflowType);
  const communityGroupWorkflowsHook = useCommunityGroupWorkflows(!isForCommunity ? entityId : undefined, workflowType);

  // Hooks for upserting workflows
  const communityUpsertWorkflowHook = useUpsertCommunityWorkflow();
  const communityGroupUpsertWorkflowHook = useUpsertCommunityGroupWorkflow(!isForCommunity ? entityId : undefined);

  const { workflows: workflowConfig, loading, errors } = isForCommunity ? communityWorkflowsHook : communityGroupWorkflowsHook;
  const {
    upsertWorkflow,
    loading: isDeleting,
    errors: deleteErrors,
    resetErrors,
  } = isForCommunity ? communityUpsertWorkflowHook : communityGroupUpsertWorkflowHook;

  const topicActions = useMemo(() => workflowConfig?.topicActions || [], [workflowConfig?.topicActions]);

  // Determine title and description based on workflow type
  const title = isClassificationWorkflow(workflowType) ? 'Classification Workflow' : 'Moderation Workflow';
  const description = isClassificationWorkflow(workflowType)
    ? 'Define automated content classification by mapping topics to safety actions.'
    : 'Configure automated reporting and escalation by mapping topics to safety actions.';

  const [selectedTopicAction, setSelectedTopicAction] = useState<TopicAction | undefined>(undefined);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isMoreInfoDialogOpen, setIsMoreInfoDialogOpen] = useState(false);

  const updateWorkflowConfig = useCallback(
    async (newTopicActions: TopicAction[]): Promise<boolean> => {
      try {
        const updatedConfig = workflowConfig
          ? { ...workflowConfig, topicActions: newTopicActions }
          : {
              type: workflowType,
              topicActions: newTopicActions,
              // Communities need API settings, community groups don't
              ...(isForCommunity && {
                apiSettings: {
                  maxTps: 100,
                  accessClients: [],
                },
              }),
            };

        const result = await upsertWorkflow(entityId, workflowType, updatedConfig);

        return result === true;
      } catch (error) {
        console.error('Failed to update workflow config:', error);

        return false;
      }
    },
    [entityId, workflowConfig, workflowType, isForCommunity, upsertWorkflow]
  );

  const handleCloseDialog = useCallback(() => {
    setIsDialogOpen(false);
    setSelectedTopicAction(undefined);
  }, []);

  const handleOpenDialog = useCallback((topicAction?: TopicAction) => {
    setSelectedTopicAction(topicAction || undefined);
    setIsDialogOpen(true);
  }, []);

  // Create the delete item context
  const deleteItemContext = isEntityAdmin
    ? {
        handler: async (topicAction: TopicAction): Promise<boolean> => {
          const newTopicActions = topicActions.filter((ta: TopicAction) => ta.topicName !== topicAction.topicName);

          return await updateWorkflowConfig(newTopicActions);
        },
        isDeleting,
        errors: deleteErrors,
        resetErrors,
      }
    : undefined;

  const columns = [
    {
      key: 'topicName',
      header: 'Topic',
      render: (topicAction: TopicAction) => topicAction.topicName,
      width: '30%',
    },
    {
      key: 'actionNames',
      header: 'Actions',
      render: (topicAction: TopicAction) => (
        <div className={styles.badgeContainer}>
          {topicAction.actionNames.length > 0 ? topicAction.actionNames.map((actionName: string) => <Badge key={actionName} text={actionName} />) : '-'}
        </div>
      ),
      width: '70%',
    },
  ];

  return (
    <div>
      <SectionHeader
        description={description || `Configure topic actions for ${workflowType} workflow.`}
        endComponent={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <IconButton
              aria-label={`Learn more about ${workflowType} workflow`}
              buttonSize={IconButtonSize.Medium}
              icon={Info16}
              iconSize={IconSize.XSmall}
              onClick={() => setIsMoreInfoDialogOpen(true)}
            />
            {isEntityAdmin && <Button onClick={() => handleOpenDialog()}>ADD TOPIC ACTION</Button>}
          </div>
        }
        title={`${title} (${topicActions.length})`}
      />

      {/* Topic Actions List */}
      <DataList
        columns={columns}
        data={topicActions}
        dataTypeName="topic action"
        deleteItemContext={deleteItemContext}
        error={errors}
        isLoading={loading}
        onEdit={isEntityAdmin ? handleOpenDialog : undefined}
      />

      {/* Create/Edit Topic Action Dialog */}
      <TopicActionDialog
        entityId={entityId}
        entityType={entityType}
        isOpen={isDialogOpen}
        topicAction={selectedTopicAction}
        workflowType={workflowType}
        onClose={handleCloseDialog}
      />

      {/* More Info Dialog */}
      <WorkflowMoreInfoDialog isOpen={isMoreInfoDialogOpen} workflowType={workflowType} onClose={() => setIsMoreInfoDialogOpen(false)} />
    </div>
  );
}
